import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Injector,
    input,
    output,
    signal
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MultiLineCustomTooltipDirective } from 'src/app/shared/directives/multi-line-custom-tooltip.directive';
import { JobFulfillmentType, JobFulfillmentTypes } from '../../models/enums/job-fulfillment-type.enum';
import {
    JobFulfillmentTypeLookup,
    JobFulfillmentTypeSelections,
    JobFulfillmentTypeSetting
} from '../../models/job-fulfillment-type-models';
import { mergeArrays } from '../../utilities/merge-arrays';
import { JobFulfillmentTypeSelectorConfirmationComponent } from './job-fulfillment-type-selector-confirmation.component';

@Component({
    selector: 'nova-job-fulfillment-type-selector',
    templateUrl: './job-fulfillment-type-selector.component.html',
    styleUrls: ['./job-fulfillment-type-selector.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatFormFieldModule,
        MatSelectModule,
        MatIconModule,
        ReactiveFormsModule,
        MultiLineCustomTooltipDirective,
        MatButtonModule
    ]
})
export class JobFulfillmentTypeSelectorComponent {
    private readonly injector = inject(Injector);

    canEdit = input(false);
    confirmBeforeSave = input(false);
    confirmTitle = input<string>();
    confirmMessage = input<string>();
    jobFulfillmentTypeSelections = input.required<JobFulfillmentTypeSelections>();
    jobFulfillmentTypeExclusions = input<number[]>([]);
    allowEditWithoutAvailableFulfillmentTypes = input(false);

    selectionsChanged = output<JobFulfillmentTypeSetting[]>();

    selectedTypesControl: FormControl<number[]>;
    defaultTypeControl: FormControl<number>;
    editMode = signal(false);
    hasChanges = signal(false);
    JobFulfillmentTypeEnum = JobFulfillmentType;
    JobFulfillmentTypesList = JobFulfillmentTypes;
    originalJobFulfillmentTypeSelections: JobFulfillmentTypeSelections = { selected: [], default: null };
    currentJobFulfillmentTypeSelections = signal<JobFulfillmentTypeSelections>({ selected: [], default: null });
    private lastSyncedInputSelections: JobFulfillmentTypeSelections = { selected: [], default: null };
    availableTypes = computed<JobFulfillmentTypeLookup[]>(() => {
        return this.JobFulfillmentTypesList.filter(
            (x) => !this.jobFulfillmentTypeExclusions().includes(x.id)
        ).map<JobFulfillmentTypeLookup>((x) => ({ ...x }));
    });

    constructor(private readonly dialog: MatDialog) {
        // Initialize form controls
        this.selectedTypesControl = new FormControl<number[]>([], Validators.required);
        this.defaultTypeControl = new FormControl<number>(null, Validators.required);

        // Keep controls in sync with input changes
        afterNextRender(() => {
            effect(
                () => {
                    const selections = this.jobFulfillmentTypeSelections();
                    this.jobFulfillmentTypeExclusions();
                    const normalizedSelections: JobFulfillmentTypeSelections = {
                        selected: [...(selections.selected ?? [])],
                        default: selections.default
                    };

                    if (this.editMode()) {
                        return;
                    }

                    if (this.areSelectionsEqual(normalizedSelections, this.lastSyncedInputSelections)) {
                        return;
                    }

                    this.selectedTypesControl.setValue([...normalizedSelections.selected], { emitEvent: false });
                    this.defaultTypeControl.setValue(normalizedSelections.default, { emitEvent: false });
                    this.originalJobFulfillmentTypeSelections = {
                        selected: [...normalizedSelections.selected],
                        default: normalizedSelections.default
                    };
                    this.currentJobFulfillmentTypeSelections.set({
                        selected: [...normalizedSelections.selected],
                        default: normalizedSelections.default
                    });
                    this.lastSyncedInputSelections = {
                        selected: [...normalizedSelections.selected],
                        default: normalizedSelections.default
                    };
                    this.hasChanges.set(false);
                },
                { injector: this.injector, allowSignalWrites: true }
            );
        });
    }

    private areSelectionsEqual(a: JobFulfillmentTypeSelections, b: JobFulfillmentTypeSelections): boolean {
        if (a.default !== b.default) {
            return false;
        }

        const aSelected = [...(a.selected ?? [])].sort((x, y) => x - y);
        const bSelected = [...(b.selected ?? [])].sort((x, y) => x - y);

        return (
            aSelected.length === bSelected.length &&
            aSelected.every((selection, index) => selection === bSelected[index])
        );
    }

    get viewModeText() {
        return this.selectedTypeIds
            .sort((a, b) => ((b === this.defaultTypeId) as any) - ((a === this.defaultTypeId) as any))
            .map((id) => {
                const lookup = this.availableTypes().find((a) => a.id === id);
                let text: string;
                if (lookup) {
                    text = id === this.defaultTypeId ? `${lookup.name} (Default)` : lookup.name;
                }
                return text;
            })
            .filter((x) => !!x)
            .join(', ');
    }

    get selectionsTriggerText(): string {
        return this.availableTypes()
            .filter((x) => this.selectedTypesControl.value.includes(x.id))
            .map((x) => x.name)
            .join(', ');
    }

    get defaultTriggerText(): string {
        return this.availableTypes().find((x) => x.id === this.defaultTypeControl.value)?.name;
    }

    get availableDefaultTypes(): JobFulfillmentTypeLookup[] {
        return this.availableTypes().filter((x) => this.selectedTypesControl.value.includes(x.id));
    }

    get saveButtonDisabled(): boolean {
        return (
            !this.hasChanges() ||
            this.selectedTypesControl.hasError('required') ||
            this.defaultTypeControl.hasError('required')
        );
    }

    private get selectedTypeIds(): number[] {
        return this.currentJobFulfillmentTypeSelections().selected ?? [];
    }

    private get defaultTypeId(): number | null {
        return this.currentJobFulfillmentTypeSelections().default;
    }

    selectionsChangedEvent() {
        if (!this.selectedTypesControl.value.includes(this.defaultTypeControl.value)) {
            this.defaultTypeControl.setValue(undefined);
            this.defaultTypeControl.markAsTouched();
        }

        this.hasChanges.set(true);
    }

    defaultSelectionChangedEvent() {
        this.hasChanges.set(true);
    }

    saveChanges() {
        if (this.confirmBeforeSave()) {
            const confirmDlgRef = this.dialog.open(JobFulfillmentTypeSelectorConfirmationComponent, {
                data: {
                    title: this.confirmTitle(),
                    message: this.confirmMessage()
                },
                maxWidth: '25vw',
                panelClass: 'nova__dialog'
            });

            confirmDlgRef.afterClosed().subscribe((confirmed) => {
                if (confirmed) {
                    this._saveChanges();
                }
            });
        } else {
            this._saveChanges();
        }
    }

    cancelEditMode() {
        this.selectedTypesControl.setValue(this.selectedTypeIds);
        this.defaultTypeControl.setValue(this.defaultTypeId);
        this.editMode.set(false);
        this.hasChanges.set(false);
    }

    private _saveChanges() {
        const selectedTypesIds = this.selectedTypesControl.value;
        const selectedTypes = this.availableTypes()
            .filter((x) => selectedTypesIds.includes(x.id))
            .map((x) => x.id);

        const defaultTypeId = this.defaultTypeControl.value;
        const defaultType = this.availableTypes().find((x) => x.id === defaultTypeId)?.id;
        const combinedIds = mergeArrays(this.originalJobFulfillmentTypeSelections.selected, selectedTypes);
        const typesToUpsert: JobFulfillmentTypeSetting[] = [];

        // we need to make sure the list of data we send contains all possible types

        // add selected
        for (const selectedId of combinedIds) {
            typesToUpsert.push({
                jobFulfillmentTypeId: selectedId,
                isActive: selectedTypes.some((x) => x === selectedId),
                isDefault: selectedId === defaultType
            });
        }

        // add unselected
        for (const algoId of JobFulfillmentTypes.map((x) => x.id)) {
            if (!combinedIds.includes(algoId)) {
                typesToUpsert.push({
                    jobFulfillmentTypeId: algoId,
                    isActive: false,
                    isDefault: false
                });
            }
        }

        this.originalJobFulfillmentTypeSelections = {
            selected: [...selectedTypes],
            default: defaultType
        };
        this.currentJobFulfillmentTypeSelections.set({
            selected: [...selectedTypes],
            default: defaultType
        });

        this.selectionsChanged.emit(typesToUpsert);
        this.editMode.set(false);
        this.hasChanges.set(false);
    }

    onEditClicked() {
        this.editMode.set(true);
        if (this.allowEditWithoutAvailableFulfillmentTypes() && this.selectedTypesControl.value?.length === 0) {
            this.selectedTypesControl.setValue([1]);
            if (!this.defaultTypeControl.value) {
                this.defaultTypeControl.setValue(1);
            }
            this.hasChanges.set(true);
        }
    }
}
